import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Compass, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Spinner, ScoreBadge, StageBadge, PlatformTag, EmptyState } from "@/components/app/widgets";

const QUEUES = [
  { key: "follow_up_today", label: "Follow Up Today", accent: "text-[#D4AF37]", border: "border-[#D4AF37]/40" },
  { key: "upcoming", label: "Upcoming (7 days)", accent: "text-white", border: "border-white/15" },
  { key: "waiting_for_response", label: "Waiting for Response", accent: "text-amber-400", border: "border-amber-500/30" },
  { key: "partnership_opportunities", label: "Partnership Opportunities", accent: "text-emerald-400", border: "border-emerald-500/30" },
  { key: "high_priority_relationships", label: "High-Priority Relationships", accent: "text-[#D4AF37]", border: "border-[#D4AF37]/30" },
  { key: "no_action_required", label: "No Action Required", accent: "text-zinc-500", border: "border-white/10" },
];

export default function FollowUps() {
  const [queues, setQueues] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/follow-ups/queues").then((r) => setQueues(r.data)).catch(() => setQueues(null));
  useEffect(() => { load(); }, []);

  const recommend = async (prospectId) => {
    setBusy(prospectId);
    try {
      const res = await api.post(`/prospects/${prospectId}/follow-up/recommend`);
      toast.success(`AI recommends: ${res.data.recommendation.action.replace(/_/g, " ")}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const complete = async (prospectId) => {
    try {
      await api.post(`/prospects/${prospectId}/follow-up/complete`);
      toast.success("Follow-up completed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!queues) return <Spinner label="Loading follow-up queues" />;

  const total = Object.values(queues).reduce((s, q) => s + q.length, 0);

  return (
    <div data-testid="followups-page">
      <PageHeader title="Follow-Up Manager" sub="Your next-action queue — no relationship goes cold, no prospect gets spammed." />

      {total === 0 ? (
        <EmptyState icon={CalendarClock} title="No active relationships yet" desc="Discover prospects, then research, score and engage them — your follow-up queues will fill themselves." />
      ) : (
        <div className="grid gap-6 xl:grid-cols-2">
          {QUEUES.map((q) => (
            <section key={q.key} data-testid={`queue-${q.key}`} className={`border ${q.border} bg-[#0A0A0A] p-6`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-xs font-bold uppercase tracking-[0.2em] ${q.accent}`}>{q.label}</h2>
                <span className="rounded-full bg-white/5 px-2.5 py-0.5 text-xs font-bold text-zinc-300">{queues[q.key].length}</span>
              </div>
              <div className="mt-4 space-y-3">
                {queues[q.key].length === 0 && <p className="text-sm text-zinc-600">Nothing here right now.</p>}
                {queues[q.key].map((p) => (
                  <div key={p.prospect_id} data-testid={`queue-item-${p.prospect_id}`} className="border border-white/10 p-4">
                    <div className="flex items-center gap-3">
                      <Link to={`/app/prospects/${p.prospect_id}`} className="min-w-0 flex-1 truncate text-sm font-medium text-white hover:text-[#D4AF37]">
                        {p.name}
                      </Link>
                      <PlatformTag platform={p.platform} />
                      <ScoreBadge score={p.score} />
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
                      <StageBadge stage={p.status} />
                      {p.follow_up?.due_date && (
                        <span className="flex items-center gap-1">
                          <CalendarClock className="h-3 w-3" /> due {p.follow_up.due_date}
                        </span>
                      )}
                    </div>
                    {p.follow_up?.recommendation && (
                      <p className="mt-2 border-l-2 border-[#D4AF37]/50 pl-3 text-xs leading-relaxed text-zinc-400">
                        <span className="font-semibold uppercase tracking-wider text-[#D4AF37]">
                          {p.follow_up.recommendation.action.replace(/_/g, " ")}:
                        </span>{" "}
                        {p.follow_up.recommendation.what_to_do || p.follow_up.recommendation.reason}
                      </p>
                    )}
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => recommend(p.prospect_id)} disabled={busy === p.prospect_id}
                        data-testid={`recommend-${p.prospect_id}`}
                        className="flex items-center gap-1 rounded-full border border-[#D4AF37]/50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-50"
                      >
                        {busy === p.prospect_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Compass className="h-3 w-3" />}
                        What should I do?
                      </button>
                      {p.follow_up?.status && p.follow_up.status !== "completed" && (
                        <button
                          onClick={() => complete(p.prospect_id)}
                          data-testid={`complete-${p.prospect_id}`}
                          className="flex items-center gap-1 rounded-full border border-emerald-500/40 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-400 hover:bg-emerald-500/10"
                        >
                          <Check className="h-3 w-3" /> Done
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
